import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Building, 
  MapPin, 
  CreditCard,
  Calendar,
  AlertTriangle,
  Package
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent, StatusBadge, Skeleton } from '@components/Card';
import { Dialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { Tabs, TabItem } from '@/components/Tabs';
import { ProviderProductsList } from '@/components/ProviderProductsList';
import { providerService } from '../../services/providerService';
import { ProviderExtended as Provider } from '../../types/provider';
import { ProviderBankCounterparts } from './ProviderBankCounterparts';
import { ProviderPaymentTermCard } from './ProviderPaymentTermCard';

/**
 * Página de detalhes do fornecedor
 * Exibe todas as informações do fornecedor em formato somente leitura
 */
export const ProviderDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadProvider(id);
    }
  }, [id]);

  const loadProvider = async (providerId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const providerData = await providerService.getProviderById(parseInt(providerId));
      setProvider(providerData as Provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fornecedor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!provider) return;
    
    try {
      setDeleting(true);
      await providerService.deleteProvider(provider.provider_id);
      navigate('/providers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir fornecedor');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Formatar documento
  const formatDocument = (document: string | undefined | null, type: 'cnpj' | 'cpf') => {
    if (!document) return '';
    if (type === 'cnpj' && document.length === 14) {
      return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    if (type === 'cpf' && document.length === 11) {
      return document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    return document;
  };

  // Formatar CEP
  const formatCEP = (cep: string | undefined | null) => {
    if (!cep) return '';
    if (cep.length === 8) {
      return cep.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }
    return cep;
  };

  // Formatar telefone
  const formatPhone = (phone: string | undefined | null) => {
    if (!phone) return '';
    if (phone.length === 11) {
      return phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    if (phone.length === 10) {
      return phone.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    if (!provider) return null;
    return (
      <StatusBadge 
        status={provider.is_active ? 'ativo' : 'inativo'} 
      />
    );
  };

  const getPixKeyLabel = (type: string) => {
    const types: Record<string, string> = {
      'cpf': 'CPF',
      'cnpj': 'CNPJ',
      'email': 'E-mail',
      'phone': 'Telefone',
      'random': 'Chave Aleatória'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/providers')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        
        <div className="grid gap-6">
          <Card>
            <CardContent>
              <Skeleton className="h-40" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/providers')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Fornecedor não encontrado</h1>
          </div>
        </div>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => id && loadProvider(id)} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!provider) {
    return null;
  }

  const docType = (provider.federal_tax_id?.length ?? 0) === 14 ? 'cnpj' : 'cpf';

  const tabs: TabItem[] = [
    {
      id: 'company',
      label: 'Empresa',
      icon: <Building size={16} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Razão Social</p>
              <p className="font-medium text-slate-900">{provider.company_name || '—'}</p>
            </div>
            {provider.trade_name && provider.trade_name !== provider.company_name && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Nome Fantasia</p>
                <p className="font-medium text-slate-900">{provider.trade_name}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{docType === 'cnpj' ? 'CNPJ' : 'CPF'}</p>
              <p className="font-medium font-mono text-slate-900">
                {provider.federal_tax_id ? formatDocument(provider.federal_tax_id, docType) : '—'}
              </p>
            </div>
            {provider.state_tax_id && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Inscrição Estadual</p>
                <p className="font-medium font-mono text-slate-900">{provider.state_tax_id}</p>
              </div>
            )}
            {provider.phone && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Telefone</p>
                <p className="font-medium text-slate-900">{formatPhone(provider.phone)}</p>
              </div>
            )}
            {provider.email && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">E-mail</p>
                <p className="font-medium text-blue-600">{provider.email}</p>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'address',
      label: 'Endereço',
      icon: <MapPin size={16} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {provider.postal_code && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">CEP</p>
                <p className="font-medium font-mono text-slate-900">{formatCEP(provider.postal_code)}</p>
              </div>
            )}
            {provider.address && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Endereço</p>
                <p className="font-medium text-slate-900">{provider.address}</p>
              </div>
            )}
            {provider.address_number && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Número</p>
                <p className="font-medium text-slate-900">{provider.address_number}</p>
              </div>
            )}
            {provider.complement && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Complemento</p>
                <p className="font-medium text-slate-900">{provider.complement}</p>
              </div>
            )}
            {provider.neighborhood && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Bairro</p>
                <p className="font-medium text-slate-900">{provider.neighborhood}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Cidade</p>
              <p className="font-medium text-slate-900">{provider.city || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Estado</p>
              <p className="font-medium text-slate-900">{provider.state || '—'}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'payment',
      label: 'Pagamento',
      icon: <CreditCard size={16} />,
      content: (
        <div className="space-y-6">
          <ProviderPaymentTermCard providerId={provider.provider_id} />

          <hr className="border-slate-200" />
          {provider.pix_key ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Chave PIX</p>
                <p className="font-medium font-mono text-slate-900">{provider.pix_key}</p>
              </div>
              {provider.pix_key_type && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tipo</p>
                  <p className="font-medium text-slate-900">{getPixKeyLabel(provider.pix_key_type)}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Nenhuma chave PIX cadastrada.</p>
          )}

          <hr className="border-slate-200" />
          <ProviderBankCounterparts providerId={provider.provider_id} />

          {(provider.bank_name || provider.bank_account) && (
            <>
              <hr className="border-slate-200" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {provider.bank_name && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Banco</p>
                    <p className="font-medium text-slate-900">{provider.bank_name}</p>
                  </div>
                )}
                {provider.bank_branch && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Agência</p>
                    <p className="font-medium font-mono text-slate-900">{provider.bank_branch}</p>
                  </div>
                )}
                {provider.bank_account && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Conta</p>
                    <p className="font-medium font-mono text-slate-900">
                      {provider.bank_account}{provider.bank_account_digit && `-${provider.bank_account_digit}`}
                    </p>
                  </div>
                )}
                {provider.account_holder_name && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Titular</p>
                    <p className="font-medium text-slate-900">{provider.account_holder_name}</p>
                    {provider.account_holder_document && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatDocument(provider.account_holder_document,
                          provider.account_holder_document.length === 14 ? 'cnpj' : 'cpf'
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'products',
      label: 'Produtos',
      icon: <Package size={16} />,
      content: (
        <ProviderProductsList providerId={provider.provider_id} />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/providers')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-h1">{provider.company_name}</h1>
              {getStatusBadge()}
            </div>
            <p className="text-slate-600">
              {provider.trade_name && provider.trade_name !== provider.company_name
                ? provider.trade_name
                : provider.federal_tax_id
                  ? formatDocument(provider.federal_tax_id, docType)
                  : ''
              }
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <PermissionGuard permission="providers.write">
            <Button
              variant="outline"
              icon={<Edit size={20} />}
              onClick={() => navigate(`/providers/${provider.provider_id}/edit`)}
            >
              Editar
            </Button>
          </PermissionGuard>
          
          <PermissionGuard permission="providers.delete">
            <Button
              variant="outline"
              icon={<Trash2 size={20} />}
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
            >
              Excluir
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Tabs Card */}
      <Card>
        <CardContent>
          <Tabs tabs={tabs} defaultTab="company" />
        </CardContent>
      </Card>

      {/* Informações de auditoria */}
      <Card>
        <CardHeader title="Informações de Sistema" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Criação</h4>
              <div className="flex items-center gap-2">
                <Calendar className="text-slate-400" size={16} />
                <span className="text-sm text-slate-600">
                  {provider.created_at ? formatDate(provider.created_at) : '—'}
                </span>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Última Modificação</h4>
              <div className="flex items-center gap-2">
                <Calendar className="text-slate-400" size={16} />
                <span className="text-sm text-slate-600">
                  {provider.updated_at ? formatDate(provider.updated_at) : '—'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Confirmar exclusão"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Tem certeza que deseja excluir o fornecedor{' '}
            <strong>{provider.company_name}</strong>?
          </p>
          <p className="text-sm text-red-600">
            Esta ação não pode ser desfeita e removerá todas as informações do fornecedor.
          </p>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Excluindo...' : 'Excluir Fornecedor'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};